/**
 * AV-17958: presentation rules for the Activity lens, shared by the
 * Accounts and Organisations lists and the history dialog. Pure functions,
 * unit-tested, no Angular.
 */

export interface ActivitySummary {
    range: number;
    rangeLabel: string;
    baselineLabel: string;
    interactions: number;
    interactionsDelta: number | null;
    logins: number;
    loginsDelta: number | null;
    activeUsers: number;
    totalUsers: number;
    contactsAdded: number;
    lastActivityAt: string | null;
    trend: string | null;
    sparkline: number[];
    accountCount?: number;
    activeAccountCount?: number;
}

/** Deltas inside ±this read as flat: no arrow, no colour. */
export const FLAT_WITHIN = 5;

export const TREND_LABELS: { [key: string]: string } = {
    dropped: "Dropped",
    quiet: "Quiet",
    steady: "Steady",
    growing: "Growing",
    new: "New",
    nodata: "No data",
};

export const TREND_ICONS: { [key: string]: string } = {
    dropped: "trending_down",
    quiet: "pause",
    growing: "trending_up",
};

/** null trend = no rollup row = "nodata". */
export function trendKey(trend: string | null | undefined): string {
    return trend && TREND_LABELS[trend] ? trend : "nodata";
}

export function trendLabel(trend: string | null | undefined): string {
    return TREND_LABELS[trendKey(trend)];
}

export function trendIcon(trend: string | null | undefined): string {
    return TREND_ICONS[trendKey(trend)] || "";
}

/** "▲ 12% vs 4wk avg" / "▼ 62% vs 4wk avg" / "— Flat vs 4wk avg" / "— no baseline". */
export function deltaText(
    delta: number | null | undefined,
    baselineLabel: string
): string {
    if (delta === null || delta === undefined) return "— no baseline";
    if (Math.abs(delta) < FLAT_WITHIN) return "— Flat " + baselineLabel;
    return (delta > 0 ? "▲ " : "▼ ") + Math.abs(delta) + "% " + baselineLabel;
}

/**
 * Colour class for a delta: up is green, flat and small drops grey, 20-50%
 * down orange, worse than 50% red.
 */
export function deltaClass(delta: number | null | undefined): string {
    if (delta === null || delta === undefined) return "ec-delta-muted";
    if (delta >= FLAT_WITHIN) return "ec-delta-up";
    if (delta > -20) return "ec-delta-muted";
    if (delta > -50) return "ec-delta-warn";
    return "ec-delta-down";
}

/** Whole days between an ISO date/datetime and now (never negative). */
export function daysSince(
    iso: string | null | undefined,
    now: Date = new Date()
): number | null {
    if (!iso) return null;
    const then = new Date(iso.replace(" ", "T"));
    if (isNaN(then.getTime())) return null;
    const ms = now.getTime() - then.getTime();
    return Math.max(0, Math.floor(ms / 86400000));
}

export function relativeDays(
    iso: string | null | undefined,
    now: Date = new Date()
): string {
    const days = daysSince(iso, now);
    if (days === null) return "Never";
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return days + " days ago";
}

/** Text colour ramp for last activity: normal to 14 days, orange to 30, red after. */
export function lastActivityClass(
    iso: string | null | undefined,
    now: Date = new Date()
): string {
    const days = daysSince(iso, now);
    if (days === null || days > 30) return "ec-age-old";
    if (days > 14) return "ec-age-stale";
    return "";
}

/**
 * SVG polyline points for a sparkline: `values` oldest first, drawn across
 * `width` x `height` with a little vertical padding so the stroke never
 * clips. Empty input gives an empty string.
 */
export function sparkPoints(values: number[], width = 96, height = 28): string {
    if (!values || values.length === 0) return "";
    const max = Math.max(1, ...values);
    const pad = 2;
    const usable = height - pad * 2;
    const step = values.length > 1 ? width / (values.length - 1) : 0;
    return values
        .map((v, i) => {
            const x = values.length > 1 ? i * step : width / 2;
            const y = pad + usable - (v / max) * usable;
            return x.toFixed(1) + "," + y.toFixed(1);
        })
        .join(" ");
}

/** The final point of a sparkline, for the end dot. */
export function sparkLast(
    values: number[],
    width = 96,
    height = 28
): { x: number; y: number } | null {
    if (!values || values.length === 0) return null;
    const pts = sparkPoints(values, width, height).split(" ");
    const [x, y] = pts[pts.length - 1].split(",").map(Number);
    return { x, y };
}

const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

/** "Sep 07" from an ISO date or MySQL datetime; empty when unparseable. */
export function shortDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso.replace(" ", "T"));
    if (isNaN(d.getTime())) return "";
    return MONTHS[d.getMonth()] + " " + ("0" + d.getDate()).slice(-2);
}

/** "Wed 10 Sep" for day buckets, "04 Sep" for week buckets (the header says "Week starting"). */
export function bucketLabel(
    iso: string | null | undefined,
    bucket: string
): string {
    if (!iso) return "";
    const d = new Date(iso.replace(" ", "T"));
    if (isNaN(d.getTime())) return iso;
    const day = ("0" + d.getDate()).slice(-2) + " " + MONTHS[d.getMonth()];
    if (bucket === "week") return day;
    const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return DAYS[d.getDay()] + " " + day;
}

export function rangeLabel(range: number | undefined): string {
    switch (range) {
        case 1:
            return "24h";
        case 30:
            return "30d";
        case 90:
            return "90d";
        default:
            return "7d";
    }
}

export function rangeWords(range: number | undefined): string {
    switch (range) {
        case 1:
            return "24 hours";
        case 30:
            return "30 days";
        case 90:
            return "90 days";
        default:
            return "7 days";
    }
}

/** Empty-state copy for a trend chip on the Activity lens. */
export function emptyCopy(
    trend: string | undefined,
    noun: string,
    range: number | undefined
): string {
    const period =
        range === 1
            ? "in the last 24 hours"
            : "this " + (range === 7 || !range ? "week" : "period");
    switch (trend) {
        case "dropped":
            return "No " + noun + " have dropped " + period + ".";
        case "quiet":
            return "No " + noun + " are quiet " + period + ".";
        case "growing":
            return "No " + noun + " are growing " + period + ".";
        case "new":
            return "No new " + noun + " " + period + ".";
        case "nodata":
            return "Every " + noun.replace(/s$/, "") + " has a snapshot.";
        default:
            return "Nothing matches these filters.";
    }
}

/** Trend counts from a fully loaded list (organisations are unpaginated). */
export function countTrends(rows: any[]): { [key: string]: number } {
    const counts: { [key: string]: number } = {};
    rows.forEach((row) => {
        const key = trendKey(row.activity ? row.activity.trend : null);
        counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
}

export interface ChartBucket {
    bucketStart: string;
    interactions: number;
    logins: number;
}

/**
 * nvd3 multiChart series for the history dialog: interactions on the left
 * axis, logins on the right, plus a flat "period average" line. Buckets
 * arrive newest first; the chart wants oldest first, indexed 0..n-1 so the
 * x axis can label them.
 */
export function chartSeries(
    buckets: ChartBucket[],
    colours: { interactions: string; logins: string; average: string }
): any[] {
    const rows = buckets.slice().reverse();
    const avg = rows.length
        ? Math.round(
              rows.reduce((s, b) => s + (b.interactions || 0), 0) / rows.length
          )
        : 0;
    return [
        {
            key: "Interactions",
            type: "line",
            yAxis: 1,
            color: colours.interactions,
            values: rows.map((b, i) => ({ x: i, y: b.interactions || 0 })),
        },
        {
            key: "Logins",
            type: "line",
            yAxis: 2,
            color: colours.logins,
            values: rows.map((b, i) => ({ x: i, y: b.logins || 0 })),
        },
        {
            key: "Period average",
            type: "line",
            yAxis: 1,
            color: colours.average,
            classed: "ec-act-nv-average",
            values: rows.map((b, i) => ({ x: i, y: avg })),
        },
    ];
}
